import { linkOptions } from '@tanstack/react-router'
import * as v from 'valibot'

import { createPersistedSearchParams } from '@/core/utils/localStorage/createPersistedSearchParams'

export const defaultQueryValues = {
  page: 0,
  size: 10,
  query: '',
  sort_by: 'invitation_date' as const,
  order: 'desc' as const,
  roles: [],
  license_provider_email: '',
  license_provider_id: '',
  states: [],
  from_invited_at: '',
  to_invited_at: '',
  department_id: '',
  hidden_columns: [],
  column_order: [],
}

const paginationSchema = v.object({
  page: v.optional(v.number(), defaultQueryValues.page),
  size: v.optional(v.number(), defaultQueryValues.size),
})

const sortingSchema = v.object({
  sort_by: v.optional(
    v.picklist([
      'status',
      'invitation_date',
      'department',
      'email',
      'first_name',
      'last_name',
      'license_provider_email',
      'role',
      'available_licenses',
      'total_licenses',
    ]),
    defaultQueryValues.sort_by,
  ),
  order: v.optional(v.picklist(['asc', 'desc']), defaultQueryValues.order),
})

const filterSchema = v.object({
  query: v.optional(v.string(), defaultQueryValues.query),
  roles: v.optional(
    v.array(v.picklist(['company_administrator', 'manager', 'student'])),
    defaultQueryValues.roles,
  ),
  from_invited_at: v.optional(v.string(), defaultQueryValues.from_invited_at),
  to_invited_at: v.optional(v.string(), defaultQueryValues.to_invited_at),
  license_provider_email: v.optional(
    v.string(),
    defaultQueryValues.license_provider_email,
  ),
  license_provider_id: v.optional(
    v.string(),
    defaultQueryValues.license_provider_id,
  ),
  states: v.optional(
    v.array(v.picklist(['active', 'pending', 'deactivated'])),
    defaultQueryValues.states,
  ),
  department_id: v.optional(v.string(), defaultQueryValues.department_id),
})

const configurableColumns = [
  'email',
  'department',
  'available_licenses',
  'invitation_date',
  'role',
  'status',
] as const

const columnsSchema = v.object({
  hidden_columns: v.optional(
    v.array(v.picklist(configurableColumns)),
    defaultQueryValues.hidden_columns,
  ),
  column_order: v.optional(
    v.array(v.string()),
    defaultQueryValues.column_order,
  ),
})

export type UsersListConfigurableColumn = (typeof configurableColumns)[number]

export const usersListQueryParamsSchema = v.object({
  ...paginationSchema.entries,
  ...sortingSchema.entries,
  ...filterSchema.entries,
  ...columnsSchema.entries,
})

const persistedSearchParams = createPersistedSearchParams(
  'users-list-search-params',
  usersListQueryParamsSchema,
)

/* Store users list search params in local storage */
export const persistUsersListSearchParams = persistedSearchParams.persist

/** Links to the users list, restoring the last-used search params. */
export const usersListLinkOptions = linkOptions({
  to: '/company/users',
  search: persistedSearchParams.get,
})
